import { ApiError, type ApiClient } from '../../api/client';
import type { AbsenceSettingsPort, AbsenceSettingsHolidayCalendar, AbsenceSettingsTimeOffPlan, AbsenceSettingsTimeOffType, AbsenceSettingsPlanAssignment, AbsenceSettingsGrantRule, AbsenceSettingsHolidayDay, AbsenceSetupTargets } from '../../features/absence/public';
import { collectOffsetPages } from '../../shared/collectOffsetPages';

export function createAbsenceSettingsHttpAdapter(client: ApiClient): AbsenceSettingsPort {
  const collection = async <T extends { id: string }>(resource: string, options?: { signal?: AbortSignal }): Promise<T[]> => (
    await collectOffsetPages((offset) => client.requestTenant<{ total: number; data: T[] }>(`/api/absenceManagement/v5/{tenant}/${resource}?limit=100&offset=${offset}`, options))
  ).data;
  return {
    previewPolicy(planId, { asOf, workerId, offset = 0, signal }) {
      const query = new URLSearchParams({ asOf, limit: '50', offset: String(offset) });
      if (workerId) query.set('workerID', workerId);
      return client.requestTenant(`/api/hr/v1/{tenant}/absence/timeOffPlans/${encodeURIComponent(planId)}/preview?${query}`, { signal });
    },
    getTypeImpact(typeId) {
      return client.requestTenant(`/api/hr/v1/{tenant}/absence/timeOffTypes/${encodeURIComponent(typeId)}/impact`);
    },
    async searchWorkers(query) {
      return (await client.listWorkers(query)).data.map((worker) => ({
        id: worker.id, descriptor: worker.descriptor?.trim() || '이름 미등록',
        employeeNumber: worker.workerId,
        organization: worker.primaryJob?.supervisoryOrganization?.descriptor,
      }));
    },
    async listSetupTargets() {
      const targets: AbsenceSetupTargets = { companies: [], organizations: [], warnings: [] };
      const read = async (kind: 'companies' | 'organizations', load: () => Promise<{ data: { id: string; descriptor?: string }[] }>) => {
        try { targets[kind] = (await load()).data.map(({ id, descriptor }) => ({ id, descriptor })); }
        catch (cause) {
          if (!(cause instanceof ApiError) || cause.status !== 403) throw cause;
          targets.warnings!.push(kind === 'companies' ? '회사 조회 권한이 없어 회사별 대상을 선택할 수 없습니다.' : '조직 조회 권한이 없어 조직별 대상을 선택할 수 없습니다.');
        }
      };
      await Promise.all([
        read('companies', () => client.listHireCompanies()),
        read('organizations', () => client.listAllSupervisoryOrganizations()),
      ]);
      return targets;
    },
    previewSetup(configuration) {
      return client.requestTenant('/api/hr/v1/{tenant}/absence/setup/preview', { method: 'POST', body: JSON.stringify(configuration) });
    },
    applySetup(configuration, fingerprint, idempotencyKey) {
      return client.requestTenant('/api/hr/v1/{tenant}/absence/setup/apply', {
        method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ configuration, fingerprint }),
      });
    },
    isDefinitiveSetupFailure(cause) {
      return cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && cause.status !== 408;
    },
    async listTimeOffTypes() {
      return collection<AbsenceSettingsTimeOffType>('timeOffTypes');
    },
    async createTimeOffType(input) {
      await client.createTimeOffType(input);
    },
    async updateTimeOffType(id, input) {
      await client.updateTimeOffType(id, input);
    },
    async listHolidayCalendars() {
      return collection<AbsenceSettingsHolidayCalendar>('holidayCalendars');
    },
    getKoreanPublicHolidays(year, options) {
      const query = new URLSearchParams({ year: String(year), impactLimit: String(options?.limit ?? 50), impactOffset: String(options?.offset ?? 0) });
      return client.requestTenant(`/api/hr/v1/{tenant}/absence/holidayCalendars/korea?${query}`);
    },
    syncKoreanPublicHolidays(year) {
      return client.requestTenant('/api/hr/v1/{tenant}/absence/holidayCalendars/korea/sync', {
        method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify({ year }),
      });
    },
    async createHolidayCalendar(input) {
      await client.createHolidayCalendar(input);
    },
    async listHolidayDays(calendarId) {
      return collection<AbsenceSettingsHolidayDay>(`holidayCalendars/${encodeURIComponent(calendarId)}/days`);
    },
    async createHolidayDay(calendarId, input) {
      await client.createHolidayDay(calendarId, input);
    },
    async listTimeOffPlans() {
      return collection<AbsenceSettingsTimeOffPlan>('timeOffPlans');
    },
    async createTimeOffPlan(input) {
      return client.createTimeOffPlan(input);
    },
    async createTimeOffPlanVersion(planId, input) {
      await client.createTimeOffPlanVersion(planId, input);
    },
    async listTimeOffPlanAssignments(planId, options) {
      return collection<AbsenceSettingsPlanAssignment>(`timeOffPlans/${encodeURIComponent(planId)}/assignments`, options);
    },
    async createTimeOffPlanAssignment(planId, input) {
      await client.createTimeOffPlanAssignment(planId, input);
    },
    async listTimeOffGrantRules(planId) {
      return collection<AbsenceSettingsGrantRule>(`timeOffPlans/${encodeURIComponent(planId)}/grantRules`);
    },
    async createTimeOffGrantRule(planId, input) {
      await client.createTimeOffGrantRule(planId, input);
    },
    async runAbsenceMaintenance(asOf) {
      const result = await client.runAbsenceMaintenance(asOf);
      return {
        plansProcessed: result.plansProcessed,
        entitlementsCreated: result.entitlementsCreated,
        transactionsCreated: result.transactionsCreated,
      };
    },
  };
}
